"use client";

import { NFONE_APP_STORE_URL } from "@newform/constants";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import type { CreatorLocale } from "@/i18n/creator/locales";
import { cn } from "@/lib/utils";

/**
 * Apple's "Download on the App Store" badge, as a link to the iOS app.
 *
 * The artwork is Apple's and ships unmodified from `public/one/app-store/`
 * (see the README there for why it bypasses `<BrandIcon>`). Two images rather
 * than a recolour: Apple publishes a black lockup for light grounds and a
 * white one for dark, and the theme class decides which is shown. The lockup
 * also follows the creator locale, since the words are part of the badge.
 *
 * 56px tall, well past Apple's 40px web minimum, since on the sign-in page
 * it is the one object that is not text and the creative direction was to
 * let it carry weight. The width follows the artwork's own 119.66:40 ratio.
 * `width`/`height` reserve the box before the file arrives so the form
 * doesn't shift under a hovering finger. `unoptimized`
 * because the image route re-encodes nothing for an SVG anyway, and the
 * badge must reach the browser exactly as Apple drew it.
 */
export function AppStoreBadge({ className }: { className?: string }) {
  const t = useTranslations("signIn.appStore");
  const locale = useLocale() as CreatorLocale;
  return (
    <a
      href={NFONE_APP_STORE_URL}
      target="_blank"
      rel="noopener"
      className={cn(
        "inline-flex rounded-nf-ds-md outline-hidden",
        "transition-opacity duration-150 ease-nf-ds-out",
        "hover:opacity-90 active:opacity-70",
        "focus-visible:ring-2 focus-visible:ring-nf-ds-brand focus-visible:ring-offset-2 focus-visible:ring-offset-nf-ds-bg",
        "motion-reduce:transition-none",
        className,
      )}
    >
      <Image
        src={`/one/app-store/${locale}-black.svg`}
        alt={t("badgeAlt")}
        width={167}
        height={56}
        unoptimized
        className="h-14 w-auto dark:hidden"
      />
      <Image
        src={`/one/app-store/${locale}-white.svg`}
        alt={t("badgeAlt")}
        width={167}
        height={56}
        unoptimized
        className="hidden h-14 w-auto dark:block"
      />
    </a>
  );
}
